import { call, put, takeLatest } from 'redux-saga/effects';
import {
    waInitCheckSuccess,
    waInitCheckFailed,
    gatewayConnectSuccess,
    gatewayConnectFailed,
} from '../actions/Whatsapp/Gateway';
import { config, GetToken } from 'src/helpers/request';

// ============ API CALLS - REAL DATA ============

// API untuk cek status server
const checkServerStatusApi = async () => {
    console.log('🌐 API: checkServerStatusApi called');

    try {
        const target_url = config.baseApi;
        const token = GetToken();

        const response = await fetch(target_url + '/whatsapp/status', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(token && {
                    Authorization: 'Bearer ' + token?.token,
                }),
            },
        });

        console.log('🌐 API: Response status:', response.status);

        // Jika 404, lempar error dengan status 404
        if (response.status === 404) {
            throw new Error('API endpoint /whatsapp/status not found (404). Please check your backend API.');
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('🌐 API: Response data:', data);

        // Pastikan response memiliki struktur yang benar
        return {
            success: true,
            data: data?.data || data,
            statusCode: response.status,
        };
    } catch (error) {
        console.error('❌ API Error:', error.message);
        // Re-throw error agar ditangani oleh saga
        throw error;
    }
};

// API untuk connect WhatsApp
const connectWhatsAppApi = async (sessionId) => {
    console.log('🌐 API: connectWhatsAppApi called with sessionId:', sessionId);

    try {
        const target_url = config.baseApi;
        const token = GetToken();

        const response = await fetch(target_url + '/whatsapp/connect', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && {
                    Authorization: 'Bearer ' + token?.token,
                }),
            },
            body: JSON.stringify({ sessionId }),
        });

        console.log('🌐 API: Connect response status:', response.status);

        if (response.status === 404) {
            throw new Error('API endpoint /wa/connect not found (404). Please check your backend API.');
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('🌐 API: Connect response data:', data);

        return {
            success: true,
            data: data?.data || data,
            statusCode: response.status,
        };
    } catch (error) {
        console.error('❌ Connect API Error:', error.message);
        throw error;
    }
};

// ============ SAGAS ============

// Saga untuk cek status server
function* checkServerStatusSaga() {

    try {
        // Panggil API real
        const result = yield call(checkServerStatusApi);

        if (!result?.success) {
            throw new Error(result?.error || 'Failed to check server status');
        }
        const data = result.data || result;

        // Dispatch success dengan data real dari API
        yield put(waInitCheckSuccess({
            serverOnline: true,
            baileysConnected: data?.connected === true || data?.status === 'connected',
            authenticated: data?.authenticated === true || data?.auth === true,
            sessionCount: data?.sessionCount,
            sessions: data?.sessions || [],
            version: data?.version || '1.0.0',
            selectedGateway: data?.gatewayType,
            activeWa: data?.activeWa,
            statistics: data?.statistics,
            rawData: data, // Simpan data asli untuk debug
            timestamp: new Date().toISOString(),
        }));

        console.log('✅ Saga: waInitCheckSuccess dispatched');
        return { success: true, data };

    } catch (error) {
        console.error('❌ Saga: checkServerStatusSaga error:', error);

        // Kirim error ke reducer
        yield put(waInitCheckFailed(
            error.message || 'Failed to check server status'
        ));

        return {
            success: false,
            error: error.message,
            statusCode: error.statusCode
        };
    }
}

// Saga untuk connect WhatsApp
function* connectWhatsAppSaga(action) {
    console.log('🔄 Saga: connectWhatsAppSaga started with action:', action);

    try {
        const { sessionId } = action.payload || {};

        if (!sessionId) {
            throw new Error('sessionId is required');
        }

        // Panggil API real
        const result = yield call(connectWhatsAppApi, sessionId);
        console.log('🔄 Saga: Connect API result:', result);

        if (!result?.success) {
            throw new Error(result?.error || 'Failed to connect');
        }

        const data = result.data || result;

        // Dispatch success dengan data real dari API
        yield put(gatewayConnectSuccess({
            sessionId: sessionId,
            phone: data?.phone || data?.number || '-',
            status: data?.status || 'connected',
            qrCode: data?.qrCode || null,
            rawData: data,
            timestamp: new Date().toISOString(),
        }));

        console.log('✅ Saga: gatewayConnectSuccess dispatched');
        return { success: true, data };

    } catch (error) {
        console.error('❌ Saga: connectWhatsAppSaga error:', error);

        yield put(gatewayConnectFailed(
            error.message || 'Failed to connect to WhatsApp'
        ));

        return {
            success: false,
            error: error.message
        };
    }
}

// ============ WATCHERS ============

export function* watchCheckServerStatus() {
    console.log('👀 watcher: watchCheckServerStatus registered');
    yield takeLatest('WA_INIT_CHECK_START', checkServerStatusSaga);
}

export function* watchConnectWhatsApp() {
    console.log('👀 watcher: watchConnectWhatsApp registered');
    yield takeLatest('GATEWAY_CONNECT', connectWhatsAppSaga);
}

// ============ ROOT SAGA ============

export default function* waGatewaySaga() {
    console.log('🚀 waGatewaySaga root started');
    yield takeLatest('WA_INIT_CHECK_START', checkServerStatusSaga);
    yield takeLatest('GATEWAY_CONNECT', connectWhatsAppSaga);
}